<template>
    <v-dialog v-model="value" max-width="950px" scrollable persistent @click:outside="close">
        <v-card class="rounded-xl overflow-hidden">
            <v-card-title class="teal darken-4 py-4 px-6 d-flex align-center border-bottom">
                <v-icon left color="white">mdi-clipboard-list-outline</v-icon>
                <div class="text-h6 font-weight-bold white--text">
                    {{ isEdit || headerCreated ? 'THIẾT LẬP DANH MỤC PHIẾU YÊU CẦU' : 'TẠO MỚI PHIẾU YÊU CẦU TÀI SẢN' }}
                </div>
                <v-spacer></v-spacer>
                <v-btn icon dark @click="close" class="mr-n2">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pa-0">
                <!-- Header Info Section -->
                <v-sheet class="grey lighten-5 pa-6 border-bottom">
                    <v-form lazy-validation ref="form" v-model="valid" :readonly="headerCreated">
                        <v-row align="center">
                            <v-col cols="12" md="1" class="text-center d-none d-md-block">
                                <v-avatar color="teal lighten-5" size="48">
                                    <v-icon color="teal darken-2">mdi-office-building</v-icon>
                                </v-avatar>
                            </v-col>
                            <v-col cols="12" md="7">
                                <v-autocomplete 
                                    outlined dense 
                                    :items="departments" 
                                    item-text="name" 
                                    item-value="code" 
                                    v-model="form.departmentCode" 
                                    label="Phòng ban / Chi nhánh yêu cầu" 
                                    placeholder="Chọn đơn vị yêu cầu tài sản..."
                                    :rules="[v=>!!v|| 'Dữ liệu bắt buộc']"
                                    background-color="white"
                                    class="rounded-lg font-weight-bold"
                                    hide-details="auto"
                                ></v-autocomplete>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-btn v-if="!headerCreated" block color="teal darken-2" dark height="44" class="rounded-lg elevation-2 font-weight-bold" @click="save" :loading="loading">
                                    Tiếp tục & Mở bảng nhập <v-icon right>mdi-arrow-right</v-icon>
                                </v-btn>
                                <v-chip v-else color="success" label dark block height="44" class="rounded-lg font-weight-bold justify-center" style="width: 100%;">
                                    <v-icon left>mdi-check-circle</v-icon> Đã khởi tạo phiếu
                                </v-chip>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-sheet>

                <!-- Detail Items Section -->
                <v-expand-transition>
                    <v-sheet class="pa-0 mt-n1" v-if="headerCreated">
                        <!-- Search & Tooling -->
                        <v-toolbar flat height="64" class="px-6 border-bottom grey lighten-4">
                            <v-icon left color="teal darken-1">mdi-magnify</v-icon>
                            <v-text-field
                                v-model="detailSearch"
                                placeholder="Tìm tài sản, dụng cụ trong danh mục..."
                                hide-details
                                dense
                                solo
                                flat
                                background-color="transparent"
                                class="mt-0"
                            ></v-text-field>
                            <v-spacer></v-spacer>
                            <div class="subtitle-2 teal--text text--darken-4 font-weight-bold mr-6">
                                Tiến độ: {{ completedCount }} / {{ rows.length }}
                            </div>
                            <div class="caption grey--text font-italic d-none d-sm-block">
                                * Nhập số lượng và nhấn ngoài ô để tự động lưu
                            </div>
                        </v-toolbar>

                        <!-- Items Table -->
                        <v-data-table 
                            :headers="detailHeaders" 
                            :items="mergedItems" 
                            :items-per-page="-1"
                            hide-default-footer
                            :search="detailSearch"
                            :loading="loading"
                            loading-text="Đang tải dữ liệu, vui lòng chờ..."
                            class="elevation-0"
                            fixed-header
                            height="450px"
                        >
                            <template v-slot:[`item.property`]="{ item }">
                                <div class="subtitle-2 font-weight-bold blue-grey--text text--darken-4 py-1">{{ item.property }}</div>
                                <div class="caption grey--text mt-n1">{{ item.propertyCode }}</div>
                            </template>

                            <template v-slot:[`item.quantity`]="{ item }">
                                <div class="d-flex align-center justify-center my-1" style="max-width: 140px; margin: 0 auto;">
                                    <v-text-field
                                        v-model.number="item.quantity"
                                        type="number"
                                        outlined dense hide-details
                                        background-color="white"
                                        class="rounded"
                                        @change="onQuantityChange(item)"
                                        :loading="item.isLoading"
                                        min="0"
                                    >
                                        <template v-slot:append>
                                            <span class="caption grey--text">{{ getUnit(item.propertyCode) }}</span>
                                        </template>
                                    </v-text-field>
                                </div>
                            </template>

                            <template v-slot:[`item.actualQuantity`]="{ item }">
                                <div class="d-flex align-center justify-center my-1" style="max-width: 140px; margin: 0 auto;">
                                    <v-text-field
                                        v-model.number="item.actualQuantity"
                                        type="number"
                                        outlined dense
                                        :error="item.actualQuantity === null || item.actualQuantity === undefined || item.actualQuantity === ''"
                                        background-color="orange lighten-5"
                                        class="rounded"
                                        hide-details
                                        @input="isSaved = false"
                                        :loading="item.isLoading"
                                        min="0"
                                        placeholder="Bắt buộc"
                                    >
                                         <template v-slot:append>
                                            <span class="caption orange--text">{{ getUnit(item.propertyCode) }}</span>
                                        </template>
                                    </v-text-field>
                                </div>
                            </template>

                            <template v-slot:[`item.status`]="{ item }">
                                <v-icon v-if="item.actualQuantity !== null && item.actualQuantity !== undefined && item.actualQuantity !== ''" color="success">mdi-check-circle-outline</v-icon>
                                <v-icon v-else color="grey lighten-3">mdi-circle-outline</v-icon>
                            </template>
                        </v-data-table>
                    </v-sheet>
                </v-expand-transition>
            </v-card-text>

            <v-divider></v-divider>
            <v-card-actions class="pa-4 px-6 grey lighten-4">
                <v-btn text color="grey darken-1" class="font-weight-bold px-4" @click="close">Hủy bỏ</v-btn>
                <v-spacer></v-spacer>
                <v-divider vertical class="mx-4"></v-divider>
                <v-btn 
                    :color="canFinish ? (isSaved ? 'success' : 'teal darken-4') : 'grey lighten-3'" 
                    :dark="canFinish"
                    class="rounded-pill px-8 font-weight-bold" 
                    :class="canFinish ? 'elevation-3' : 'elevation-0 grey--text text--darken-1'"
                    @click="canFinish && !isSaved ? completeTicket() : null" 
                    v-if="headerCreated"
                    :style="!canFinish || isSaved ? 'cursor: not-allowed;' : ''"
                    :loading="saving"
                >
                    <v-icon left :color="canFinish ? 'white' : 'grey darken-1'">{{ isSaved ? 'mdi-check-circle' : 'mdi-check-all' }}</v-icon>
                    {{ canFinish ? (isSaved ? 'ĐÃ HOÀN THÀNH' : 'HOÀN THÀNH') : 'CHƯA HOÀN TẤT' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
const googleQRURL = "https://chart.googleapis.com/chart?";
import http from '@/components/services/http-common';
import moment from 'moment/moment';
const FETCH_DEPARTMENT_URL = ""
export default {
    props:["value", "item"],
    watch:{
        value: {
            handler(val){
                if(val)
                {
                    // this.fetchContractType();
                    // if(this.employee){
                    //     this.fetchEmployee(this.employee);
                    //     this.form.employeeAcode = this.employee;
                    // }
                    console.log("check is edit")
                    if(this.isEdit)
                    {
                        this.loading = true;
                        this.isSaved = true;
                        this.fetch().then(()=> {
                            return this.fetchDetail();
                        }).finally(() => {
                            this.loading = false;
                        });
                        this.headerCreated = true;
                    }
                }
                else{
                    this.close();
                }
            }
        },

        "form.name":{
            handler(val){
                this.form.params = this.convertToSlug(val);
            }
        }
    },
    computed: {
        // dialog: {
        //     get: function () {
        //         return this.value;
        //     },
        //     set: function (val){
        //         this.$emit("input", val)
        //     }
        // },
        isEdit() {
            return !!this.item;
        },
        validDetail(){
            console.log(this.rules.required);
        }
    },
    data() {
        return {
            endpoint: `https://private-label.trungsonpharma.com/`,
            qrSrc: "",
            valid: false,
            form: {
                code: "",
                departmentCode: "",
            },
            defaultForm: {
                code: "",
                departmentCode: "",
            },

            /**header */
            departments: [],
            /** Detail Header*/
            detailHeaders: [
                { text: "Thông tin tài sản / Dụng cụ", value: "property", width: "40%", sortable: true },
                { text: "Số lượng yêu cầu", value: "quantity", width: "20%", align: "center" },
                { text: "Số lượng thực tế", value: "actualQuantity", width: "25%", align: "center" },
                { text: "Trạng thái", value: "status", width: "10%", align: "center", sortable: false },
            ],
            properties: [],
            detailSearch: "",
            rows: [],
            detailItems: [],
            loading: false,
            saving: false,
            isSaved: false,

            rules: {
                required: value => !!value || 'Required.'
            },

            headerCreated: false,
        }
    },
    methods: {
        getUnit(property) {
            var data = this.properties.find(x => x.code == property);
            if (!data) return ""
            return data.propertyUnitName
        },
        close() {
            this.form = Object.assign({}, this.defaultForm);
            this.headerCreated = false;
            this.rows = [];
            this.detailItems = [];
            this.isSaved = false;
            this.$refs.form.resetValidation();
            this.$emit('close-dialog');
        },

        save() {
            if (this.$refs.form.validate()) {
                this.loading = true;
                return http.post("propertyRequests", this.form).then(res => {
                    this.headerCreated = true;
                    this.form.code = res.data.data.code;
                    this.$emit("fetchData", res.data.data.code);
                    // Sau khi tạo header không kéo detail về
                    this.syncRows();
                }).finally(() => {
                    this.loading = false;
                });
            }
        },
        fetch() {
            let url = `propertyRequests/${this.item.code}`;
            return http.get(url).then(res => {
                this.form = Object.assign({}, res.data.data);
            });
        },
        fetchDepartment() {
            return http.get("propertyRequests/get-departments").then(res => {
                this.departments = res.data;
            });
        },
        fetchProperty() {
            this.loading = true;
            return http.get("properties").then(res => {
                this.properties = res.data.items;
                if (this.headerCreated) this.syncRows();
            }).finally(() => {
                this.loading = false;
            });
        },
        fetchDetail() {
            this.loading = true;
            let url = `propertyRequests/detail/${this.form.code}`;
            return http.get(url).then(res => {
                this.detailItems = res.data;
                this.syncRows();
            }).finally(() => {
                this.loading = false;
            });
        },
        syncRows() {
            if (this.properties.length === 0) return;
            this.rows = this.properties.map(p => {
                const detail = this.detailItems.find(d => d.propertyCode === p.code);
                return {
                    property: p.name,
                    propertyCode: p.code,
                    quantity: detail ? detail.quantity : 0,
                    actualQuantity: detail ? detail.actualQuantity : null,
                    isLoading: false
                }
            }).sort((a, b) => {
                // Đưa các món có số lượng > 0 lên trên cùng để dễ xem
                if (a.quantity > 0 && b.quantity === 0) return -1;
                if (a.quantity === 0 && b.quantity > 0) return 1;
                return a.property.localeCompare(b.property);
            });
        },
        completeTicket() {
            this.saving = true;
            let url = `propertyRequests/detail/${this.form.code}`;
            
            // Bulk save all items concurrently
            const promises = this.rows.map(item => {
                return http.post(url, {
                    propertyCode: item.propertyCode,
                    quantity: item.quantity,
                    actualQuantity: item.actualQuantity,
                });
            });

            Promise.all(promises).then(() => {
                this.$emit("fetchData", this.form.code);
                this.isSaved = true;
                this.fetchDetail();
            }).catch(err => {
                console.error("Error saving items", err);
            }).finally(() => {
                this.saving = false;
            });
        },
        onQuantityChange(item) {
            // Khi thay đổi SL yêu cầu, reset SL thực tế về null để buộc người dùng nhập lại
            item.actualQuantity = null;
            // KHÔNG autoSave ở đây, chờ người dùng nhập số lượng thực tế mới lưu
        }
    },
    computed: {
        isEdit() {
            return !!this.item;
        },
        mergedItems() {
            return this.rows;
        },
        completedCount() {
            return this.rows.filter(r => r.actualQuantity !== null && r.actualQuantity !== undefined && r.actualQuantity !== "").length;
        },
        canFinish() {
            if (!this.headerCreated) return false;
            if (this.rows.length === 0) return false;
            return this.completedCount === this.rows.length;
        }
    },
    mounted() {
        this.fetchDepartment();
        this.fetchProperty();
    }
}
</script>